import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  Container,
  Paper,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Navbar from "../../../../globals/components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../../../globals/utils/api";
import InputMask from "react-input-mask";
import debounce from "lodash.debounce";
import { EMAIL_DEBOUNCE_TIMEOUT } from "../../../../globals/utils/constants";
import { useSelector } from 'react-redux';

const EditInternalUser = () => {
  const navigate = useNavigate();
  const { internal_user_id } = useParams();
  const [permissionLevel, setPermissionLevel] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true); // New state for GET request loading
  const [loadingResend, setLoadingResend] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
  });

  // Email existence check state
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const [isPiiUser, setIsPiiUser] = useState(false);
  const [isCriticalUser, setIsCriticalUser] = useState(false);

  const featuresList = [
    { label: "Internal Users", value: "internal_users" },
    { label: "Law Firms", value: "law_firms" },
    { label: "Law Firm User Management", value: "law_firm_users" },
    { label: "Bond Numbers", value: "bond_numbers" },
    { label: "Law Schools", value: "law_schools" },
    { label: "Professional Guardians", value: "professional_guardians" },
    { label: "Carriers", value: "carriers" },
    { label: "Premiums", value: "premiums" },
    { label: "States", value: "states" },
    { label: "Bond Types", value: "bond_types" },
    { label: "Obligees", value: "obligees" },
    { label: "Enhancement Request", value: "enhancement_request" },
    { label: "Analytics", value: "analytics" },
    { label: "Bond Archives", value: "bond_archives" },
    { label: "Historic Bonds Creation", value: "create_historic_bonds" },
  ];
  const [responseGlobal, setResponseGlobal] = useState(null);

  const userdetails = useSelector((state) => state.auth.user);
  const isCritical = userdetails?.is_critical_user;

  // Debounced function to check email existence
  const debouncedCheckEmail = React.useRef(
    debounce(async (email) => {
      if (!email) {
        setEmailExists(false);
        setEmailChecking(false);
        setEmailDialogOpen(false);
        return;
      }
      setEmailChecking(true);
      try {
        await apiClient("/email-exists", "POST", { email });
        setEmailExists(false);
      } catch (e) {
        if (e?.detail === "Email already exists") {
          setEmailDialogOpen(true);
          setEmailExists(true);
        } else {
          setEmailExists(false);
        }
      } finally {
        setEmailChecking(false);
      }
    }, EMAIL_DEBOUNCE_TIMEOUT)
  ).current;

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await apiClient(
          `/get-internal-user/${internal_user_id}`,
          "GET"
        );
        setResponseGlobal(response);
        if (response) {
          setFormData({
            firstName: response.user.first_name || "",
            lastName: response.user.last_name || "",
            phoneNumber: response.phonenumber || "",
            email: response.user.email || "",
          });
          setPermissionLevel(response.is_admin ? "Admin" : "User");
          setPermissions(response.features || []);
          setIsPiiUser(!!response.is_pii_user);
          setIsCriticalUser(!!response.is_critical_user);
        }
      } catch (error) {
        console.error("Error fetching user details:", error.message || error);
        toast.error(
          "Error fetching user details: " + (error.message || "Unknown error")
        );
      } finally {
        setLoadingData(false); // Stop loading indicator after data fetch
      }
    };
    fetchUserDetails();
  }, [internal_user_id]);

  const handleResendInvite = async (event) => {
    event.preventDefault();
    setLoadingResend(true);
    try {
      const response = await apiClient(
        `/resend-internal-user-invite/${internal_user_id}`,
        "PATCH"
      );
      if (response) {
        toast.success("Internal User invite Resent successfully");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error updating user details:", error.message || error);
      toast.error("Error updating user: " + (error.message || "Unknown error"));
    } finally {
      setLoadingResend(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePermissionChange = (event) => {
    const { value, checked } = event.target;
    setPermissions((prevPermissions) =>
      checked
        ? [...prevPermissions, value]
        : prevPermissions.filter((perm) => perm !== value)
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const payload = {
      features: permissionLevel === "Admin" ? [] : permissions,
      full_name: formData.firstName + " " + formData.lastName,
      user: {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
      },
      is_admin: permissionLevel === "Admin",
      phonenumber: formData.phoneNumber,
      is_pii_user: isPiiUser,
      is_critical_user: isCriticalUser,
    };

    try {
      const response = await apiClient(
        `/internal-user/${internal_user_id}`,
        "PATCH",
        payload
      );
      if (response) {
        toast.success("Internal User updated successfully");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error updating user details:", error.message || error);
      toast.error("Error updating user: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Dialog handlers
  const handleDialogClose = () => {
    setEmailDialogOpen(false);
  };
  const handleDialogContinue = () => {
    setEmailDialogOpen(false);
    setEmailExists(false);
    // Keep the email as is, allow user to proceed
  };
  const handleDialogCancel = () => {
    setEmailDialogOpen(false);
    setFormData((prev) => ({ ...prev, email: "" }));
    setEmailExists(false);
  };

  const renderLabel = (mainText, description) => (
    <span>
      <Typography component="span" style={{ fontWeight: "bold" }}>
        {mainText}
      </Typography>{" "}
      - {description}
    </span>
  );

  return (
    <>
      <Navbar />
      <Container maxWidth="md" style={{ marginTop: "3rem" }}>
        {loadingData ? ( // Show loading spinner while loading data
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            style={{ height: "100vh" }}
          >
            <CircularProgress size={50} />
          </Grid>
        ) : (
          <Paper
            elevation={3}
            style={{
              padding: "2rem",
              backgroundColor: "#f5f5f5",
              borderRadius: "15px",
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              style={{ textAlign: "center", fontWeight: "bold" }}
            >
              Edit Internal User
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    fullWidth
                    variant="outlined"
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => {
                      handleInputChange(e);
                      debouncedCheckEmail(e.target.value);
                    }}
                    error={Boolean(emailExists)}
                    helperText={
                      emailChecking
                        ? "Checking email..."
                        : emailExists
                          ? "This email already exists."
                          : ""
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="First Name"
                    fullWidth
                    value={formData.firstName}
                    onChange={handleInputChange}
                    name="firstName"
                    variant="outlined"
                    required
                    type="text"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Last Name"
                    fullWidth
                    value={formData.lastName}
                    onChange={handleInputChange}
                    name="lastName"
                    variant="outlined"
                    required
                    type="text"
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputMask
                    mask="(999) 999-9999" // US format; change as needed
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                  >
                    {(inputProps) => (
                      <TextField
                        {...inputProps}
                        label="Phone Number"
                        fullWidth
                        name="phoneNumber"
                        variant="outlined"
                        required
                      />
                    )}
                  </InputMask>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Permission Level"
                    fullWidth
                    variant="outlined"
                    select
                    required
                    value={permissionLevel}
                    onChange={(e) => setPermissionLevel(e.target.value)}
                  >
                    <MenuItem value="User">User</MenuItem>
                    <MenuItem value="Admin">Admin</MenuItem>
                  </TextField>
                </Grid>

                {isCritical && (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        style={{ fontWeight: "bold", marginTop: "1rem" }}
                      >
                        User Access Flags
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={2} direction="column">
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isPiiUser}
                                onChange={(e) => setIsPiiUser(e.target.checked)}
                              />
                            }
                            label={renderLabel(
                              "Has PII Access",
                              "Grants access to view sensitive PII fields."
                            )}
                          />
                        </Grid>
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isCriticalUser}
                                onChange={(e) => setIsCriticalUser(e.target.checked)}
                              />
                            }
                            label={renderLabel(
                              "Critical User",
                              "Gives priveldged access to the portal."
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}

                {permissionLevel === "User" && (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        style={{ fontWeight: "bold", marginTop: "1rem" }}
                      >
                        Account Management Features
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={2} direction="column">
                        {featuresList.map((feature) => (
                          <Grid item key={feature.value}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={permissions.includes(feature.value)}
                                  onChange={handlePermissionChange}
                                  value={feature.value}
                                />
                              }
                              label={renderLabel(
                                feature.label,
                                `Allows access to manage ${feature.label.toLowerCase()}.`
                              )}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </>
                )}

                <Grid
                  item
                  xs={12}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "2rem",
                  }}
                >
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleCancel}
                    style={{ marginRight: "1rem" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={24} /> : null}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
                  {responseGlobal && responseGlobal.is_accepted !== null && (
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={
                        loadingResend || responseGlobal.is_accepted === true
                      }
                      onClick={handleResendInvite}
                      style={{ marginLeft: "1rem" }}
                      startIcon={
                        loadingResend ? <CircularProgress size={24} /> : null
                      }
                    >
                      {loadingResend ? "Resending..." : "Resend Invite"}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}
      </Container>

      {/* Email Exists Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") handleDialogClose();
        }}
      >
        <DialogTitle>Email Already Exists</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This email already exists. Would you like to continue with this email?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel} color="secondary">Cancel</Button>
          <Button onClick={handleDialogContinue} color="primary" autoFocus>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default EditInternalUser;
